import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { faPlain, toman } from 'utilities/helper/format'
import Portal from 'components/portal'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'
import Item from './item'
import {
    InstallmentRow,
    countAfterTap,
    isOverdue,
    overdueRun,
    paymentTiming,
    selectedRows,
    selectionLabel,
    selectionTotal,
    unpaidOf,
} from './selection'

interface ILoansProps {
    data: InstallmentRow[]
    /** the borrower's poolship — who the repayment is credited to */
    poolshipId?: number
    fundName?: string
}

/**
 * The installment schedule, with a basket.
 *
 * Members and managers used to be able to settle exactly one installment per
 * trip through the pay flow, so catching up on five overdue months meant five
 * round trips to the bank. Rows are now added to a selection and paid in one
 * gateway payment — see `selection.ts` for why the selection is a prefix and
 * not an arbitrary set.
 *
 * The bar under the remaining balance carries that idea: settled, selected and
 * still-owed as three bands of one line. Selecting installments grows the
 * middle band into the last one, so the member watches the selection eat into
 * the debt before committing to it — the same "selecting looks like paying"
 * signal the rail nodes give, at loan scale.
 */
export default function Loans({ data, poolshipId, fundName }: ILoansProps) {
    const navigate = useNavigate()
    const { moneyPool, loanId } = useParams()
    const { moneyPoolManagement } = useGetMoneyPoolManagement(moneyPool)

    // Managers and owners can also record a repayment they took in cash. This
    // was hardcoded false, carried over from the confirm modal this screen
    // replaced, so «ثبت پرداخت» never appeared for anyone.
    const isManager =
        moneyPoolManagement?.poolship?.role === 'mgr' ||
        moneyPoolManagement?.poolship?.role === 'own'

    const ordered = useMemo(
        () => [...data].sort((a, b) => a.index - b.index),
        [data]
    )
    const unpaid = useMemo(() => unpaidOf(ordered), [ordered])
    const overdue = useMemo(() => overdueRun(unpaid), [unpaid])

    const [selectedCount, setSelectedCount] = useState(0)

    // How often a manager's note is actually on the screen, against
    // `installment_note_expanded` — the pair says whether the notes get read
    // once they are somewhere a reader would look.
    const notedCount = ordered.filter((one) => (one.tag ?? '').trim()).length
    const hasReportedNotes = useRef(false)
    useEffect(() => {
        if (ordered.length === 0 || hasReportedNotes.current) return
        hasReportedNotes.current = true
        posthog.capture('installment_notes_shown', {
            fund_id: moneyPool,
            loan_id: loanId,
            noted_count: notedCount,
            installment_count: ordered.length,
        })
    }, [ordered.length, notedCount, moneyPool, loanId])

    const selected = selectedRows(unpaid, selectedCount)
    const isFilled = new Set(selected.map((row) => row.id))
    const total = selectionTotal(unpaid, selectedCount)
    const label = selectionLabel(unpaid, selectedCount, (n) => faPlain.format(n))

    const loanTotal = ordered.reduce((sum, row) => sum + (row.amount || 0), 0)
    const remaining = unpaid.reduce((sum, row) => sum + (row.amount || 0), 0)
    const paidCount = ordered.length - unpaid.length
    const share = (value: number) => (loanTotal > 0 ? (value / loanTotal) * 100 : 0)

    // Position in the UNPAID list, not in the schedule: the prefix is over what
    // is still owed, so an installment that is paid has no position at all.
    const positionOf = (id: number) => unpaid.findIndex((row) => row.id === id)

    const onSelect = (id: number) => {
        const position = positionOf(id)
        if (position < 0) return
        setSelectedCount(countAfterTap(selectedCount, position))
    }

    const onPay = () => {
        if (selected.length === 0 || !poolshipId) return
        posthog.capture('loan_installments_selected_for_payment', {
            fund_id: moneyPool,
            loan_id: loanId,
            count: selected.length,
            unpaid_count: unpaid.length,
            overdue_count: overdue,
            is_full_settlement: selected.length === unpaid.length,
            total,
        })
        navigate(`/easy-pay/pay-for-other-in-loan/${moneyPool}`, {
            state: {
                installments: selected.map((row) => ({
                    id: row.id,
                    amount: row.amount,
                    index: row.index,
                })),
                poolshipId,
                name: fundName,
                isManager,
                loanId,
            },
        })
    }

    const chip = (text: string, active: boolean, onClick: () => void) => (
        <button
            type="button"
            onClick={onClick}
            className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors motion-reduce:transition-none ${
                active
                    ? 'border-green-1 bg-green-1 text-white'
                    : 'border-stroke bg-white text-text-body'
            }`}
        >
            {text}
        </button>
    )

    return (
        <div
            className={`bg-canvas px-4 pt-3 ${
                selected.length > 0 ? 'pb-40' : 'pb-6'
            }`}
        >
            {/* What is left, and how much of it this basket covers. */}
            <div className="bg-white border border-divider rounded-card shadow-card p-4">
                {/* «مانده‌ی بازپرداخت», not «مانده‌ی وام»: with the fee added
                    per installment this is 28,080,000 on a 30,000,000 loan, and
                    sitting under «مبلغ وام» the shorter label reads as a
                    contradiction rather than as what is still owed. */}
                <p className="text-xs text-text-secondary text-center">
                    مانده‌ی بازپرداخت
                </p>
                <p className="mt-[2px] text-center text-[26px] font-bold text-heading leading-tight">
                    {toman(remaining)}
                </p>

                <div className="mt-4 flex h-2 w-full overflow-hidden rounded-full bg-divider">
                    <span
                        className="h-full bg-green-1 transition-all duration-300 motion-reduce:transition-none"
                        style={{ width: `${share(loanTotal - remaining)}%` }}
                    />
                    <span
                        className="h-full bg-green-1/40 transition-all duration-300 motion-reduce:transition-none"
                        style={{ width: `${share(total)}%` }}
                    />
                </div>

                <div className="mt-2 flex items-center justify-between gap-2 text-[13px]">
                    <span className="text-text-secondary">
                        {faPlain.format(paidCount)} از{' '}
                        {faPlain.format(ordered.length)} قسط پرداخت شده
                    </span>
                    {overdue > 0 && (
                        <span className="rounded-full bg-danger-soft px-2.5 py-[2px] text-xs font-bold text-danger-on">
                            {faPlain.format(overdue)} قسط سررسید شده
                        </span>
                    )}
                </div>
            </div>

            {/* The three cases people actually want: the next one, everything
                that is late, or the rest of the loan. */}
            {unpaid.length > 0 && (
                <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                    {chip('پرداخت قسط بعدی', selectedCount === 1, () =>
                        setSelectedCount(selectedCount === 1 ? 0 : 1)
                    )}
                    {overdue > 1 &&
                        chip(
                            `پرداخت ${faPlain.format(
                                overdue
                            )} قسط عقب‌افتاده`,
                            selectedCount === overdue,
                            () =>
                                setSelectedCount(
                                    selectedCount === overdue ? 0 : overdue
                                )
                        )}
                    {unpaid.length > 1 &&
                        chip(
                            `تسویه کامل (${faPlain.format(unpaid.length)} قسط)`,
                            selectedCount === unpaid.length,
                            () =>
                                setSelectedCount(
                                    selectedCount === unpaid.length
                                        ? 0
                                        : unpaid.length
                                )
                        )}
                </div>
            )}

            <p className="mt-5 mb-2 text-sm font-bold text-heading">
                برنامه‌ی اقساط
            </p>

            <div className="flex flex-col">
                {ordered.map((item, position) => {
                    // A rail segment is green only when BOTH ends are done —
                    // settled or selected. That is what makes the selection
                    // read as a continuation of the run already paid instead
                    // of a stripe floating in the middle of the schedule.
                    const filled = (row?: InstallmentRow) =>
                        !!row && (row.is_paid || isFilled.has(row.id))
                    return (
                    <Item
                        key={item.id}
                        number={item.index || position + 1}
                        jalaliDate={item.jalali_due_date ?? ''}
                        amount={item.amount}
                        isOverdue={!item.is_paid && isOverdue(item)}
                        isPayed={item.is_paid}
                        timing={paymentTiming(item)}
                        note={item.tag}
                        id={item.id}
                        isSelectable={!item.is_paid}
                        isSelected={isFilled.has(item.id)}
                        hasPrev={position > 0}
                        hasNext={position < ordered.length - 1}
                        linkAbove={
                            filled(item) && filled(ordered[position - 1])
                        }
                        linkBelow={
                            filled(item) && filled(ordered[position + 1])
                        }
                        onSelect={() => onSelect(item.id)}
                    />
                    )
                })}
            </div>

            {selected.length > 0 && (
                // Portalled to <body> on purpose. FundLayout wraps every route
                // in a framer-motion div, which keeps `transform: matrix(...)`
                // after the page-transition animation — and a transformed
                // ancestor becomes the containing block for `position: fixed`.
                // Left in place the bar pinned to the bottom of the PAGE
                // (y=1642 in a 932px viewport), so selecting installments
                // appeared to do nothing at all unless you scrolled to the end.
                <Portal>
                    <div
                        role="region"
                        aria-label="سبد پرداخت"
                        className="fixed bottom-0 left-0 right-0 z-40 md:px-[35%] animate-slide-up motion-reduce:animate-none"
                    >
                        <div className="bg-white shadow-nav px-4 pb-4 pt-3">
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="text-[13px] text-text-secondary truncate">
                                    {faPlain.format(selected.length)} قسط
                                    {label ? ` · ${label}` : ''}
                                </span>
                                <span className="text-[15px] font-bold text-heading shrink-0">
                                    {toman(total)}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={onPay}
                                disabled={!poolshipId}
                                className="mt-2 w-full rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none disabled:opacity-60"
                            >
                                ادامه به پرداخت{' '}
                                {faPlain.format(selected.length)} قسط
                            </button>
                            <button
                                type="button"
                                onClick={() => setSelectedCount(0)}
                                className="mt-1 w-full py-1 text-center text-sm text-text-secondary"
                            >
                                انصراف
                            </button>
                        </div>
                    </div>
                </Portal>
            )}
        </div>
    )
}
